import * as globals from '@wandb/weave/common/css/globals.styles';
import Color from 'color';
import uPlot from 'uplot';

import {formatXAxisNonTime, formatYAxis} from '../../../util/plotHelpers/axis';
import {
  getAxisStyleForFontSize,
  type PlotFontSize,
} from '../../../util/plotHelpers/plotFontSize';
import {type Line, type Timestep} from '../../../util/plotHelpers/types';
import {type UPlotMode} from '../../../util/rampFeatureFlags/linePlotDynamicConfigs';
import {hasMultipleSinglePointRuns} from '../../PanelRunsLinePlot/utils/checkTypes';
import {getStrokeDashArrayNonSvg, getStrokeDetail} from '../lineUtils';
import {type DomainMaybe} from '../types';
import {
  createDrawIssueLinesHook,
  type IssueLinesConfig,
} from './drawIssueLines';
import {
  createDrawNonFiniteMarkersHook,
  getNonFiniteMarkers,
  type NonFiniteMarker,
  type NonFiniteMarkerState,
} from './drawNonFiniteMarkers';
import {
  createDrawStackedAreasHook,
  getStackedAreaLines,
  type StackedAreaDrawState,
} from './drawStackedAreas';
import {getFacetData, getFacetWindow} from './facetedDrawData';
import {getBands} from './getBands';
import {getDomain} from './getDomain';
import {getIncrs} from './getIncrs';
import {type LineNameMap, type SeriesEntry} from './getSeriesEntries';
import {
  getuPlotLineGap,
  getuPlotTickFontSizePx,
  getuPlotTickGapPx,
  getuPlotTickSizePx,
  getuPlotXAxisTickValuesSizePx,
  getuPlotYAxisTickValuesSizePx,
} from './margins';
import {numAxisSplitsForUPlotAxis} from './splits';

const GRID_LINE_WIDTH_PX = 1;
const POINT_PX_SIZE = 4;
const SINGLE_POINT_PX_SIZE = 8;
export const getGridLineColor = (isDarkMode: boolean) => {
  return isDarkMode
    ? Color(globals.MOON_750).rgb().string()
    : Color(globals.MOON_250).rgb().string();
};

export type NonFiniteMarkerContext = {
  markers: NonFiniteMarker[];
  getState: () => NonFiniteMarkerState;
};

/**
 * Show a dot when a line has exactly one usable Y value in the visible chart range.
 * A lone point cannot form a line segment, so it would otherwise be invisible.
 * Lines with multiple visible points keep their usual line-only appearance.
 */
export function shouldShowSingleVisiblePoint(
  u: uPlot,
  seriesIdx: number,
  idx0: number,
  idx1: number
): boolean {
  const faceted = u.series[seriesIdx]?.facets != null;
  const facet = faceted ? getFacetData(u, seriesIdx) : undefined;
  const yValues = (facet ? facet[1] : u.data[seriesIdx]) as ArrayLike<
    number | null | undefined
  >;
  if (facet) {
    [idx0, idx1] = getFacetWindow(facet[0], u.scales.x.min, u.scales.x.max);
  }
  let finitePointCount = 0;

  for (let i = idx0; i <= idx1; i++) {
    const y = yValues[i];

    if (y != null && Number.isFinite(y) && ++finitePointCount > 1) {
      return false;
    }
  }

  return finitePointCount === 1;
}

/** Chart options without the size, which the caller adds so a resize can keep the rest. */
type UPlotOptionsWithoutSize = Omit<uPlot.Options, 'width' | 'height'>;

/** Set how the chart draws its lines, min/max bands, points, axes, and issue markers. */
export function getUPlotOptions(
  seriesEntries: (SeriesEntry | null)[],
  lineNameMap: LineNameMap,
  lines: Line[],
  getNonFiniteMarkerState: () => NonFiniteMarkerState,
  issueLinesConfig: IssueLinesConfig,
  xDomain: DomainMaybe,
  yDomain: DomainMaybe,
  xAxisIsDatetime: boolean | undefined,
  xLogScale: boolean | undefined,
  yLogScale: boolean | undefined,
  fontSize: PlotFontSize,
  timestep: Timestep | undefined,
  isDarkMode: boolean,
  isOverlay: boolean = false,
  isBaseLayer: boolean = false,
  uPlotMode: UPlotMode = 'aligned',
  getStackedAreaDrawState?: () => StackedAreaDrawState
): UPlotOptionsWithoutSize {
  const faceted = uPlotMode === 'faceted';
  const linearPaths = faceted ? uPlot.paths.linear!() : undefined;
  const pointPaths = faceted ? uPlot.paths.points!() : undefined;
  const facetedPointPaths: uPlot.Series.Points.PathBuilder | undefined =
    pointPaths
      ? (u, seriesIdx, _idx0, _idx1, filter) => {
          const xs = getFacetData(u, seriesIdx)[0];
          const [idx0, idx1] = getFacetWindow(
            xs,
            u.scales.x.min,
            u.scales.x.max
          );
          // Include the neighboring point on each side, because part of its dot
          // may still be visible at the chart's left or right edge.
          return pointPaths(
            u,
            seriesIdx,
            Math.max(idx0 - 1, 0),
            Math.min(idx1 + 1, xs.length - 1),
            filter
          );
        }
      : undefined;
  const bands = getBands(seriesEntries, lineNameMap);
  const isSinglePointChart = lines.length === 1 && lines[0].data.length === 1;
  const showEnlargedSinglePoint =
    isSinglePointChart || hasMultipleSinglePointRuns(lines);
  // Handle infinite/NaN markers
  const nonFiniteMarkers = getNonFiniteMarkers(seriesEntries);
  const drawNonFiniteMarkers = createDrawNonFiniteMarkersHook(
    nonFiniteMarkers,
    getNonFiniteMarkerState
  );

  // Handle issue lines
  const drawIssueLines = createDrawIssueLinesHook(issueLinesConfig);

  // Stacked / percent area fills are painted by a canvas hook (not uPlot bands),
  // so they render correctly even where the top and baseline edges cross —
  // which happens wherever a stacked value is negative, or for percent area — a
  // case uPlot bands cannot handle. Only the non-overlay layers paint them.
  const stackedAreaLines = getStackedAreaLines(lines);
  const drawStackedAreas =
    stackedAreaLines.length > 0
      ? createDrawStackedAreasHook(
          stackedAreaLines,
          // The overlay layer paints only the hovered run (via the getter); the
          // main layer paints all of them.
          isOverlay ? getStackedAreaDrawState : undefined
        )
      : null;

  // The highlight layer keeps every line available but starts with them hidden.
  // Hovering shows the selected lines without rebuilding the chart.
  const initialSeriesShow = !isOverlay;

  // The first position configures the X axis; the rest draw lines and band edges.
  const seriesConfigs: uPlot.Series[] = seriesEntries.map(entry => {
    if (entry == null) {
      // Position zero holds the X-axis settings, rather than a line to draw.
      return {};
    }
    const {type, line} = entry;
    const baseColor = line.color ?? 'red';
    const strokeDashArray = getStrokeDashArrayNonSvg(line.mark)?.map(
      num => num * window.devicePixelRatio
    );
    const {strokeWidth: lineStrokeWidth} = getStrokeDetail(
      line.color,
      isOverlay, // the axes are hidden in the overlay layer which is the highlighted layer
      line.lineWidth
    );

    if (type === 'band-min' || type === 'band-max') {
      // uPlot fills between these two edges to draw a min/max band.
      return {
        facets: faceted ? [{scale: 'x', sorted: 1}, {scale: 'y'}] : undefined,
        show: initialSeriesShow,
        spanGaps: true,
        stroke: Color(baseColor).rgb().string(),
        width: lineStrokeWidth,
        label: line.name,
        points: {show: false},
        paths: linearPaths,
        sorted: 1, // Tell uPlot the points run from smallest to largest X.
      };
    } else if (line.type === 'area' && line.aux !== true) {
      // Stacked / percent area line: painted as a filled polygon by the
      // drawStackedAreas hook, so the series itself draws nothing (no stroke, no
      // points). It still carries the data uPlot needs for the x-scale.
      return {
        facets: faceted ? [{scale: 'x', sorted: 1}, {scale: 'y'}] : undefined,
        show: initialSeriesShow,
        spanGaps: true,
        stroke: 'rgba(0, 0, 0, 0)',
        width: 0,
        label: line.name,
        paths: () => null,
        points: {show: false},
        sorted: 1,
      };
    } else {
      // Draw the line at full opacity here; LinePlotuPlot dims its layer when another line is highlighted.
      // A group's sample lines are colored faintly as supporting lines, but
      // react-vis's canvas lines ignore a color's alpha and draw them at full
      // strength. Match that so the dotted samples stay visible.
      const isGroupSampleLine =
        line.aux === true && line.meta.aggregation === 'samples';
      const stroke = isGroupSampleLine
        ? Color(baseColor).alpha(1).rgb().string()
        : Color(baseColor).rgb().string();
      const isPointsMark = line.mark === 'points';
      return {
        facets: faceted ? [{scale: 'x', sorted: 1}, {scale: 'y'}] : undefined,
        show: initialSeriesShow,
        spanGaps: true,
        stroke,
        width: lineStrokeWidth,
        label: line.name,
        sorted: 1, // Tell uPlot the points run from smallest to largest X.
        dash: strokeDashArray,
        paths: isPointsMark ? () => null : linearPaths, // no connecting line
        points: {
          show: isPointsMark ? true : shouldShowSingleVisiblePoint,
          // Lines with shared X values need uPlot's normal point drawing.
          ...(facetedPointPaths ? {paths: facetedPointPaths} : {}),
          size: showEnlargedSinglePoint ? SINGLE_POINT_PX_SIZE : POINT_PX_SIZE,
          width: 1,
          stroke,
          fill: stroke,
        },
      };
    }
  });

  const tickLabelStyle = {
    ...getAxisStyleForFontSize(fontSize),
    fill: isDarkMode ? globals.MOON_300 : globals.MOON_650,
  } as ReturnType<typeof getAxisStyleForFontSize>;
  const fontString = `${getuPlotTickFontSizePx(fontSize)}px ${tickLabelStyle.fontFamily}`;

  const gridLineColor = getGridLineColor(isDarkMode);
  const renderAxisChrome = isBaseLayer;
  // Only the base layer paints visible axis values; other layers draw them
  // transparent (but still generate the values so axis sizing stays identical).
  const axisValueColor = renderAxisChrome
    ? (tickLabelStyle.fill as string)
    : 'transparent';
  const sharedAxisOptions = {
    gap: getuPlotTickGapPx(),
    lineGap: getuPlotLineGap(),
    font: fontString,
    border: {
      show: renderAxisChrome,
      stroke: gridLineColor,
      width: GRID_LINE_WIDTH_PX,
    },
  };

  const xAxisValues = (function computeXAxisValues() {
    if (xAxisIsDatetime) {
      return undefined;
    }
    return function formatXAxisValues(_u: uPlot, splits: number[]) {
      if (xDomain[0] == null || xDomain[1] == null) {
        return splits;
      }
      const formatter = formatXAxisNonTime(
        xLogScale ? 'log' : 'linear',
        xDomain[0]!,
        xDomain[1]!,
        timestep
      );
      if (formatter == null) {
        return splits;
      }
      // Format every split once, collecting both the aligned labels (nulls kept
      // so the array stays 1:1 with splits, for rendering) and the non-null
      // labels (for the duplicate check) in a single pass.
      const {formattedSplits, filteredFormattedSplits} = splits.reduce<{
        formattedSplits: (string | null)[];
        filteredFormattedSplits: string[];
      }>(
        (acc, split) => {
          if (split == null) {
            acc.formattedSplits.push(null);
            return acc;
          }
          const formatted = formatter(split);
          acc.formattedSplits.push(formatted);
          acc.filteredFormattedSplits.push(formatted);
          return acc;
        },
        {formattedSplits: [], filteredFormattedSplits: []}
      );
      // Just like in react-vis https://github.com/wandb/core/pull/22703
      if (
        new Set(filteredFormattedSplits).size !== filteredFormattedSplits.length
      ) {
        // duplicate x values, so actually just return the original splits
        return splits;
      }
      return formattedSplits;
    };
  })();
  const yAxisValues = (_u: uPlot, splits: number[]) => {
    return splits.map(split => (split == null ? null : formatYAxis(split)));
  };

  const result: UPlotOptionsWithoutSize = {
    mode: faceted ? 2 : 1,
    series: seriesConfigs,
    bands,
    padding: [8, 12, 0, 0], // uPlot defaults to extra padding we don't need
    axes: [
      // x-axis
      {
        stroke: axisValueColor, // this is a string, not just any CSSProperties
        values: xAxisValues,
        grid: {
          show: false,
        },
        size: getuPlotXAxisTickValuesSizePx(fontSize),
        incrs: getIncrs(xAxisIsDatetime ?? false),
        ticks: {
          show: renderAxisChrome,
          size: getuPlotTickSizePx(),
          stroke: gridLineColor,
          width: GRID_LINE_WIDTH_PX,
        },
        splits: xLogScale ? undefined : numAxisSplitsForUPlotAxis,
        ...sharedAxisOptions,
      },
      // y-axis
      {
        stroke: axisValueColor, // this is a string, not just any CSSProperties
        values: yAxisValues,
        size: getuPlotYAxisTickValuesSizePx(fontSize),
        grid: {
          show: renderAxisChrome,
          stroke: gridLineColor,
          width: GRID_LINE_WIDTH_PX,
        },
        splits: yLogScale ? undefined : numAxisSplitsForUPlotAxis,
        incrs: getIncrs(false),
        ticks: {
          show: renderAxisChrome,
          size: getuPlotTickSizePx(),
          stroke: gridLineColor,
          width: GRID_LINE_WIDTH_PX,
          filter: (u, splits, axisIdx, foundSpace, foundIncr) =>
            u.axes[axisIdx]?.filter?.(
              u,
              splits,
              axisIdx,
              foundSpace,
              foundIncr
            ) ?? splits,
        },
        ...sharedAxisOptions,
      },
    ],
    scales: {
      x: {
        time: xAxisIsDatetime ?? false,
        distr: xLogScale ? 3 : 1, // Scale.Distr.Logarithmic = 3, Scale.Distr.Linear = 1
        range: getDomain(xDomain, xLogScale ?? false),
      },
      y: {
        distr: yLogScale ? 3 : 1, // Scale.Distr.Logarithmic = 3, Scale.Distr.Linear = 1
        range: getDomain(yDomain, yLogScale ?? false),
      },
    },
    legend: {
      show: false,
    },
    // The React crosshair overlay sits on top of the plot and handles hover and
    // brush-zoom, so uPlot's own cursor never sees a mouse event. Turning it off
    // skips creating a hover point element for every series and skips the
    // transform writes uPlot makes on those elements whenever it updates the
    // cursor. Nothing reads uPlot cursor state (no setCursor hooks, no reads of
    // u.cursor), so this only removes work.
    cursor: {
      show: false,
    },
    hooks: {
      // Stacked area fills paint first so lines/markers land on top of them.
      draw: drawStackedAreas
        ? [drawStackedAreas, drawIssueLines, drawNonFiniteMarkers]
        : [drawIssueLines, drawNonFiniteMarkers],
    },
    ms: 1, // uPlot uses seconds by default, but the Line data is in milliseconds
  };
  return result;
}
